/**
 * PATH: packages/job-hub/src/jobs/qa/useQaPointerDrag.ts
 * FILE_ID: JOBHUB-QA-POINTER-DRAG-001
 * PURPOSE: One pointer drag for desktop and phone. The grip does not use HTML5 drag.
 */
import { createContext, createElement, useContext, useRef, type PointerEvent, type ReactNode } from 'react'
import type { QaDropKind, QaDropRef } from './qaDragCommit'

function clearActive() {
  document.querySelectorAll('[data-qa-drop-active]').forEach((node) => {
    node.removeAttribute('data-qa-drop-active')
  })
}

function dropRef(node: Element | null): QaDropRef | null {
  const target = node?.closest('[data-qa-drop]')
  const raw = target?.getAttribute('data-qa-drop') ?? ''
  const split = raw.indexOf(':')
  if (!target || split < 1) return null
  const kind = raw.slice(0, split) as QaDropKind
  const id = raw.slice(split + 1)
  if (!id || !['level', 'location', 'checklist', 'row'].includes(kind)) return null
  return { kind, id }
}

export function useQaPointerDrag(onDrop: (from: QaDropRef, to: QaDropRef) => void) {
  const from = useRef<QaDropRef | null>(null)
  const onDropRef = useRef(onDrop)
  onDropRef.current = onDrop

  function onPointerDown(kind: QaDropKind, id: string, event: PointerEvent<HTMLSpanElement>) {
    from.current = { kind, id }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  function onPointerMove(event: PointerEvent<HTMLSpanElement>) {
    if (!from.current) return
    clearActive()
    const hit = document.elementFromPoint(event.clientX, event.clientY)
    hit?.closest('[data-qa-drop]')?.setAttribute('data-qa-drop-active', 'true')
  }

  function onPointerUp(event: PointerEvent<HTMLSpanElement>) {
    const source = from.current
    from.current = null
    const hit = document.elementFromPoint(event.clientX, event.clientY)
    const target = dropRef(hit)
    clearActive()
    if (!source || !target) return
    if (source.kind === target.kind && source.id === target.id) return
    onDropRef.current(source, target)
  }

  return { onPointerDown, onPointerMove, onPointerUp }
}

const QaDragContext = createContext<ReturnType<typeof useQaPointerDrag> | null>(null)

export function QaDragRoot({
  onDrop,
  children,
}: {
  onDrop: (from: QaDropRef, to: QaDropRef) => void
  children: ReactNode
}) {
  const drag = useQaPointerDrag(onDrop)
  return createElement(QaDragContext.Provider, { value: drag }, children)
}

export function useQaDragHandlers() {
  return useContext(QaDragContext)
}
